import { Card, CardContent } from "@/shared/ui/card";
import { 
  Scan, 
  Brain, 
  Calendar, 
  BarChart3,
  Users,
  Shield,
  Zap,
  Settings,
  Database,
  Camera,
  Workflow,
  AlertTriangle
} from "lucide-react";

const features = [
  {
    icon: Scan,
    title: "Robotic Imaging & OCR",
    description: "Advanced computer vision captures asset data from QR codes, equipment labels, and technical drawings with 99.7% accuracy.",
    category: "Data Capture"
  },
  {
    icon: Brain,
    title: "AI-Powered Diagnostics",
    description: "Machine learning algorithms analyze equipment patterns to predict failures before they occur, reducing downtime by 75%.",
    category: "Intelligence"
  },
  {
    icon: Calendar,
    title: "Predictive Scheduling",
    description: "Automated maintenance schedules based on manufacturer specifications, usage patterns, and environmental conditions.",
    category: "Automation"
  },
  {
    icon: BarChart3,
    title: "Engineering Analytics",
    description: "Real-time dashboards with KPIs, cost analysis, equipment lifecycle tracking, and ROI calculations.",
    category: "Analytics"
  },
  {
    icon: Database,
    title: "Asset Intelligence Hub",
    description: "Centralized database with complete equipment history, specifications, warranties, and compliance documentation.",
    category: "Data Management"
  },
  {
    icon: Workflow,
    title: "Workflow Automation",
    description: "Trigger work orders, alerts, and processes automatically based on schedules, thresholds, or AI recommendations.",
    category: "Automation"
  },
  {
    icon: Users,
    title: "Team Collaboration",
    description: "Role-based access for engineers, technicians, and vendors with real-time communication and task management.",
    category: "Collaboration"
  },
  {
    icon: Shield,
    title: "Compliance Management",
    description: "Automated regulatory compliance tracking, audit trails, and report generation for industry standards.",
    category: "Compliance"
  },
  {
    icon: Camera,
    title: "Mobile Inspection",
    description: "Field-ready mobile app for instant asset scanning, photo documentation, and offline data collection.",
    category: "Mobility"
  },
  {
    icon: AlertTriangle,
    title: "Predictive Alerts",
    description: "Smart notifications for maintenance due dates, warranty expirations, SLA violations, and anomaly detection.",
    category: "Monitoring"
  },
  {
    icon: Zap,
    title: "API Integration",
    description: "RESTful APIs and webhooks for seamless integration with existing CMMS, ERP, and building management systems.",
    category: "Integration"
  },
  {
    icon: Settings,
    title: "Custom Workflows",
    description: "Configurable business rules, approval processes, and custom fields to match your organization's procedures.",
    category: "Customization"
  }
];

export function LandingFeatures() {
  return (
    <section id="features" className="py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 mb-6">
            <Zap className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium text-primary">Enterprise Features</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
            Complete Asset Intelligence Platform
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            From robotic data capture to predictive analytics, our platform provides 
            comprehensive tools for modern facility and asset management.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <Card key={index} className="border-border hover:border-primary/50 transition-all duration-300 hover:shadow-elegant group bg-card">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 rounded-lg p-3 group-hover:bg-primary/20 transition-colors duration-300">
                    <feature.icon className="h-6 w-6 text-primary" />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-foreground">
                        {feature.title}
                      </h3>
                      <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                        {feature.category}
                      </span>
                    </div>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Technical Specifications */}
        <div className="mt-16 bg-gradient-accent rounded-2xl p-8 border border-primary/20">
          <h3 className="text-2xl font-bold text-foreground mb-6 text-center">
            Technical Specifications
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-primary mb-2">99.7%</div>
              <div className="text-sm text-muted-foreground">OCR Accuracy</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary mb-2">&lt;100ms</div>
              <div className="text-sm text-muted-foreground">API Response Time</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary mb-2">ISO 27001</div>
              <div className="text-sm text-muted-foreground">Security Certified</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary mb-2">99.9%</div>
              <div className="text-sm text-muted-foreground">Uptime SLA</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}